import React, { useState } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { useLanguage } from '../contexts/LanguageContext';
import { Switch } from '@/components/ui/switch';
import { Input } from '@/components/ui/input';
import { Briefcase, Zap, CalendarDays, Hash, X, Plus } from 'lucide-react';
import { toast } from 'sonner';

const ROLES = [
  { key: 'is_provider', icon: Briefcase, lv: 'Pakalpojumu sniedzējs', en: 'Service provider', ru: 'Поставщик услуг',
    descLv: 'Reklamē savus pakalpojumus un veido Pro profilu ar portfolio.', descEn: 'Advertise services and build a Pro profile with portfolio.', descRu: 'Рекламируйте услуги и создайте Pro-профиль.' },
  { key: 'is_gig_worker', icon: Zap, lv: 'Gig strādnieks', en: 'Gig worker', ru: 'Исполнитель халтур',
    descLv: 'Saņem paziņojumus par ātrajiem darbiem (Haltūras) tuvumā.', descEn: 'Get notified about quick gigs (Haltūras) near you.', descRu: 'Получайте уведомления о работе рядом.' },
  { key: 'is_organizer', icon: CalendarDays, lv: 'Organizators', en: 'Organizer', ru: 'Организатор',
    descLv: 'Veido pasākumus un meklē dalībniekus konkrētām lomām.', descEn: 'Create events and recruit participants for roles.', descRu: 'Создавайте мероприятия и набирайте участников.' },
];

export default function ProfileCapabilities() {
  const { user, updateProfile } = useAuth();
  const { language } = useLanguage();
  const [busy, setBusy] = useState(null);
  const tr = (lv, en, ru) => (language === 'lv' ? lv : language === 'ru' ? ru : en);

  const toggle = async (key, value) => {
    setBusy(key);
    const res = await updateProfile({ [key]: value });
    if (res.success) {
      toast.success(tr('Loma atjaunināta', 'Role updated', 'Роль обновлена'));
    } else {
      toast.error(res.error || tr('Neizdevās', 'Failed', 'Ошибка'));
    }
    setBusy(null);
  };

  const [tagInput, setTagInput] = useState('');
  const [tagBusy, setTagBusy] = useState(false);
  const tags = user?.tags || [];

  const normalizeTag = (t) => (t || '').trim().replace(/^#/, '').toLowerCase().replace(/\s+/g, '-').replace(/[^0-9a-zāčēģīķļņōŗšūž-]/g, '').slice(0, 30);

  const saveTags = async (next) => {
    setTagBusy(true);
    const res = await updateProfile({ tags: next });
    if (!res.success) toast.error(res.error || tr('Neizdevās', 'Failed', 'Ошибка'));
    setTagBusy(false);
  };

  const addTag = async () => {
    const t = normalizeTag(tagInput);
    if (!t) return;
    if (tags.includes(t)) { setTagInput(''); return; }
    setTagInput('');
    await saveTags([...tags, t]);
  };

  const removeTag = async (t) => {
    await saveTags(tags.filter((x) => x !== t));
  };

  return (
    <div className="bg-white border border-[#E5E4E0] rounded-2xl p-6 sm:p-8" data-testid="profile-capabilities">
      <h2 className="font-outfit text-xl font-semibold text-[#121212] mb-1">
        {tr('Manas lomas', 'My roles', 'Мои роли')}
      </h2>
      <p className="text-sm text-[#5C5C5C] mb-6">
        {tr('Aktivizē lomas, ko vēlies izmantot. Pircējs esi vienmēr.',
            'Activate the roles you want. You are always a buyer.',
            'Активируйте нужные роли. Покупателем вы являетесь всегда.')}
      </p>
      <div className="space-y-4">
        {ROLES.map((r) => {
          const Icon = r.icon;
          const active = !!user?.[r.key];
          return (
            <div key={r.key} className="flex items-start gap-4 p-4 rounded-xl border border-[#F3F2EE]" data-testid={`role-${r.key}`}>
              <div className={`w-10 h-10 rounded-full flex items-center justify-center shrink-0 ${active ? 'bg-[#D97736] text-white' : 'bg-[#F3F2EE] text-[#5C5C5C]'}`}>
                <Icon className="w-5 h-5" />
              </div>
              <div className="flex-1 min-w-0">
                <p className="font-medium text-[#121212]">{r[language] || r.lv}</p>
                <p className="text-sm text-[#5C5C5C]">{tr(r.descLv, r.descEn, r.descRu)}</p>
              </div>
              <Switch
                checked={active}
                disabled={busy === r.key}
                onCheckedChange={(v) => toggle(r.key, v)}
                data-testid={`switch-${r.key}`}
              />
            </div>
          );
        })}
      </div>

      {user?.is_gig_worker && (
        <div className="mt-6 pt-6 border-t border-[#F3F2EE] space-y-4" data-testid="gig-preferences">
          <div>
            <h3 className="font-outfit text-base font-semibold text-[#121212]">
              {tr('Haltūru iestatījumi', 'Gig settings', 'Настройки халтур')}
            </h3>
            <p className="text-sm text-[#5C5C5C]">
              {tr('Izvēlies, kādus darbus vēlies saņemt.', 'Choose which jobs you want to receive.', 'Выберите, какие задания получать.')}
            </p>
          </div>
          {[
            { key: 'gig_quick_enabled', lv: 'Ātrās haltūras', en: 'Quick gigs', ru: 'Быстрые халтуры', descLv: 'Darbi, kas jādara nekavējoties (ASAP).', descEn: 'Jobs needed right away (ASAP).', descRu: 'Работа срочно.' },
            { key: 'gig_scheduled_enabled', lv: 'Haltūras ar noteiktu laiku', en: 'Scheduled gigs', ru: 'Запланированные халтуры', descLv: 'Darbi ar konkrētu datumu/laiku.', descEn: 'Jobs with a set date/time.', descRu: 'Работа на конкретное время.' },
            { key: 'role_application_notifications', lv: 'Paziņojumi par lomas pieteikumu', en: 'Role application notifications', ru: 'Уведомления о заявках на роль', descLv: 'Saņem signālu, kad kāds meklē tavu profesiju.', descEn: 'Get a signal when someone looks for your profession.', descRu: 'Сигнал, когда кто-то ищет вашу профессию.' },
          ].map((s) => (
            <div key={s.key} className="flex items-start gap-4 p-4 rounded-xl border border-[#F3F2EE]" data-testid={`gigpref-${s.key}`}>
              <div className="flex-1 min-w-0">
                <p className="font-medium text-[#121212]">{s[language] || s.lv}</p>
                <p className="text-sm text-[#5C5C5C]">{tr(s.descLv, s.descEn, s.descRu)}</p>
              </div>
              <Switch
                checked={user?.[s.key] !== false}
                disabled={busy === s.key}
                onCheckedChange={(v) => toggle(s.key, v)}
                data-testid={`switch-${s.key}`}
              />
            </div>
          ))}
        </div>
      )}

      <div className="mt-6 pt-6 border-t border-[#F3F2EE]" data-testid="profile-tags-editor">
        <div className="flex items-center gap-2 mb-1">
          <Hash className="w-4 h-4 text-[#D97736]" />
          <h3 className="font-outfit text-base font-semibold text-[#121212]">
            {tr('Profesijas tagi', 'Profession tags', 'Теги профессий')}
          </h3>
        </div>
        <p className="text-sm text-[#5C5C5C] mb-4">
          {tr('Tagi (piem., #pavārs, #fotogrāfs) palīdz, kad kāds meklē tavu profesiju haltūrām vai pasākumu lomām. AI tos ierosina no taviem sludinājumiem — vari pievienot vai dzēst.',
              'Tags (e.g. #cook, #photographer) help when someone looks for your profession for gigs or event roles. AI suggests them from your listings — add or remove freely.',
              'Теги помогают, когда кто-то ищет вашу профессию.')}
        </p>
        <div className="flex flex-wrap gap-2 mb-3" data-testid="profile-tags-list">
          {tags.length === 0 && (
            <span className="text-sm text-[#9CA3AF]">{tr('Vēl nav tagu', 'No tags yet', 'Тегов пока нет')}</span>
          )}
          {tags.map((t) => (
            <span key={t} className="inline-flex items-center gap-1 px-3 py-1 rounded-full bg-[#F3F2EE] text-sm text-[#121212]" data-testid={`profile-tag-${t}`}>
              #{t}
              <button type="button" onClick={() => removeTag(t)} disabled={tagBusy} className="text-[#9CA3AF] hover:text-[#D97736]" data-testid={`remove-tag-${t}`} aria-label="remove">
                <X className="w-3.5 h-3.5" />
              </button>
            </span>
          ))}
        </div>
        <div className="flex gap-2 max-w-sm">
          <Input
            value={tagInput}
            onChange={(e) => setTagInput(e.target.value)}
            onKeyDown={(e) => { if (e.key === 'Enter') { e.preventDefault(); addTag(); } }}
            placeholder={tr('piem., pavārs', 'e.g. cook', 'напр., повар')}
            className="h-10 bg-[#FAFAFA] border-[#E5E4E0]"
            data-testid="tag-input"
          />
          <button
            type="button"
            onClick={addTag}
            disabled={tagBusy || !tagInput.trim()}
            className="h-10 px-4 rounded-lg bg-[#D97736] hover:bg-[#C06528] text-white text-sm font-medium inline-flex items-center gap-1 disabled:opacity-50"
            data-testid="add-tag-btn"
          >
            <Plus className="w-4 h-4" /> {tr('Pievienot', 'Add', 'Добавить')}
          </button>
        </div>
      </div>
    </div>
  );
}
